import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Order } from '../../../common/model/dto/order/order';
import {
  areAddressesEqual,
  getAddressFullName,
} from '../../../common/utils/services/order-address-utils';
import StringUtils from '../../../common/utils/services/string-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvOrderEditButton from '../atoms/OvOrderEditButton';
import OvCopyToClipboardButton from '../molecules/OvCopyToClipboardButton';
import OvOrderAddress from '../molecules/OvOrderAddress';
import OvRecordDetailsSection from '../molecules/OvRecordDetailsSection';

interface OvOrderCustomerSectionProps {
  order: Order;
  isUpdateAllowed: boolean;
  isEditDisabled: boolean;
  onEditShippingAddress: () => void;
}

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.substring(0, 1).toUpperCase())
    .join('');

const OvOrderCustomerSection: FC<OvOrderCustomerSectionProps> = ({
  order,
  isUpdateAllowed,
  isEditDisabled,
  onEditShippingAddress,
}) => {
  const { t } = useTranslation();
  const userPath = order.user?.id ? `/users/${order.user.id}` : undefined;
  const customerName =
    StringUtils.getPrettyUserName(order.user) ||
    getAddressFullName(order.shipping_address) ||
    getAddressFullName(order.billing_address);
  const isBillingSameAsShipping = areAddressesEqual(
    order.shipping_address,
    order.billing_address
  );

  return (
    <OvRecordDetailsSection
      title={t('orders.details.customer')}
      action={
        userPath && (
          <NavLink to={userPath}>{t('orders.details.viewUser')}</NavLink>
        )
      }
    >
      <Layout>
        <Identity>
          <Avatar aria-hidden="true">{getInitials(customerName) || '?'}</Avatar>
          <div>
            <strong>{StringUtils.displayValue(customerName)}</strong>
            <Email>
              {userPath && order.user_email ? (
                <NavLink to={userPath}>{order.user_email}</NavLink>
              ) : (
                StringUtils.displayValue(order.user_email)
              )}
              {order.user_email && (
                <OvCopyToClipboardButton
                  text={order.user_email}
                  label={t('orders.details.copyEmail')}
                />
              )}
            </Email>
          </div>
        </Identity>
        <Shipping>
          <AddressLabel>
            <h3>{t('orders.details.shippingAddress')}</h3>
            {isUpdateAllowed && (
              <OvOrderEditButton
                disabled={isEditDisabled}
                onClick={onEditShippingAddress}
              />
            )}
          </AddressLabel>
          <OvOrderAddress address={order.shipping_address} />
        </Shipping>
        <Billing open={!isBillingSameAsShipping}>
          <summary>
            {t('orders.details.billingAddress')}
            {isBillingSameAsShipping && (
              <SameAsShipping>
                {t('orders.details.billingSameAsShipping')}
              </SameAsShipping>
            )}
          </summary>
          <OvOrderAddress address={order.billing_address} />
        </Billing>
      </Layout>
    </OvRecordDetailsSection>
  );
};

export default OvOrderCustomerSection;

const Layout = styled.div`
  @media (max-width: ${breakpoints.md}) and (min-width: ${breakpoints.sm}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 1.375rem;
  }
`;

const Identity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;

  > div {
    min-width: 0;
  }
`;

const Avatar = styled.span`
  display: grid;
  place-items: center;
  flex: none;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: ${Variables.borderRadius.CIRCLE};
  background-color: ${Colours.LIGHT_GRAY_BLUE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
`;

const Email = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  overflow-wrap: anywhere;
`;

const Shipping = styled.div`
  margin-top: 0.75rem;

  @media (max-width: ${breakpoints.md}) and (min-width: ${breakpoints.sm}) {
    margin-top: 0;
  }
`;

const AddressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;

  h3 {
    margin: 0;
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
    font-weight: normal;
  }
`;

const Billing = styled.details`
  grid-column: 1 / -1;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgb(235, 237, 240);

  summary {
    font-size: ${Variables.fontSizes.SMALL};
    cursor: pointer;
  }

  summary:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  &[open] summary {
    margin-bottom: 0.5rem;
  }
`;

const SameAsShipping = styled.span`
  margin-left: 0.5rem;
  padding: 0.125rem 0.375rem;
  border-radius: ${Variables.borderRadius.SMALL};
  background-color: ${Colours.OV_BORDER_COLOR_V3};
`;
